import { Play, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { NOTIFICATION_SOUNDS, playNotificationSound } from "@/hooks/useOrderNotifier";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useSite } from "@/context/SiteContext";
import { API, errMsg } from "@/lib/api";

const WEEK_DAYS = [
  { v: 1, label: "Monday" }, { v: 2, label: "Tuesday" }, { v: 3, label: "Wednesday" },
  { v: 4, label: "Thursday" }, { v: 5, label: "Friday" }, { v: 6, label: "Saturday" },
  { v: 0, label: "Sunday" },
];
const DAY_WORDS = ["No", "One", "Two", "Three", "Four", "Five", "Six", "Seven"];

export default function AdminSettings() {
  const { refresh } = useSite();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [newStatus, setNewStatus] = useState("");
  const [testingEmail, setTestingEmail] = useState(false);
  const [newPc, setNewPc] = useState("");
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [savingSnapshot, setSavingSnapshot] = useState(false);

  useEffect(() => {
    API.get("/admin/backup/cloud/list").then((r) => setSnapshots(r.data)).catch(() => {});
  }, []);

  const saveSnapshot = async () => {
    setSavingSnapshot(true);
    try {
      const r = await API.post("/admin/backup/cloud");
      setSnapshots((prev) => [r.data, ...prev]);
      toast.success(`Cloud snapshot saved — ${r.data.records} records, safe in cloud storage`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSavingSnapshot(false);
    }
  };

  const restoreSnapshot = async (path) => {
    try {
      const r = await API.post("/admin/backup/cloud/restore", { path });
      const total = Object.values(r.data.restored || {}).reduce((a, b) => a + b, 0);
      toast.success(`Snapshot restored — ${total} records. The website now matches the snapshot exactly.`);
      await refresh();
      window.location.reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  useEffect(() => {
    API.get("/admin/settings").then((r) => setSettings(r.data)).catch((e) => toast.error(errMsg(e)));
  }, []);

  if (!settings) return <Skeleton className="h-96 rounded-xl" />;

  const business = settings.business || {};
  const delivery = settings.delivery || {};
  const paypal = settings.paypal || {};
  const seo = settings.seo || {};
  const schedule = settings.schedule || {};

  const set = (k, v) => setSettings({ ...settings, [k]: v });
  const setB = (k, v) => set("business", { ...business, [k]: v });
  const setD = (k, v) => set("delivery", { ...delivery, [k]: v });
  const notif = settings.notification || {};
  const setN = (k, v) => set("notification", { ...notif, [k]: v });

  const uploadSound = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await API.post("/admin/upload", fd);
      setN("custom_path", r.data.url || r.data.path);
      setN("custom_name", file.name);
      setN("sound", "custom");
      toast.success("Sound uploaded — remember to click Save changes");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const setP = (k, v) => set("paypal", { ...paypal, [k]: v });
  const setSeo = (k, v) => set("seo", { ...seo, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...settings,
        delivery: {
          ...delivery,
          fee: Number(delivery.fee) || 0,
          free_over: Number(delivery.free_over) || 0,
          min_order: Number(delivery.min_order) || 0,
          areas: Array.isArray(delivery.areas) ? delivery.areas : String(delivery.areas || "").split(",").map((s) => s.trim()).filter(Boolean),
        },
      };
      await API.put("/admin/settings", payload);
      setSettings(payload);
      await refresh();
      toast.success("Settings saved — the website has been updated");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const changePassword = async () => {
    try {
      await API.post("/auth/change-password", pw);
      toast.success("Password changed. Keep it somewhere safe.");
      setPw({ current_password: "", new_password: "" });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const hours = business.opening_hours || [];
  const statuses = settings.order_statuses || [];
  const deliveryDays = Array.isArray(delivery.days) && delivery.days.length ? delivery.days.map(Number) : [1, 2, 3, 4, 5, 6];
  const postcodes = Array.isArray(delivery.postcodes) ? delivery.postcodes : [];

  const toggleDay = (v) => {
    const next = deliveryDays.includes(v) ? deliveryDays.filter((d) => d !== v) : [...deliveryDays, v];
    setD("days", next);
  };

  const sendTestOrderEmail = async () => {
    setTestingEmail(true);
    try {
      const r = await API.post("/admin/test-order-email");
      toast.success(`Test order ${r.data.order_number} created and confirmation email sent to ${r.data.email}. You can delete the test order from Orders.`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setTestingEmail(false);
    }
  };

  const downloadBackup = async () => {
    try {
      const r = await API.get("/admin/backup", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      const dispo = r.headers["content-disposition"] || "";
      a.href = url;
      a.download = (dispo.match(/filename="?([^";]+)"?/) || [])[1] || "eileen-jenkins-backup.json";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup downloaded — keep it somewhere safe (e.g. Google Drive)");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const restoreBackup = async () => {
    if (!restoreFile) return;
    setRestoring(true);
    try {
      const fd = new FormData();
      fd.append("file", restoreFile);
      const r = await API.post("/admin/restore", fd);
      const total = Object.values(r.data.restored || {}).reduce((a, b) => a + b, 0);
      toast.success(`Backup restored — ${total} records. The website now matches the backup exactly.`);
      setRestoreFile(null);
      await refresh();
      window.location.reload();
    } catch (e) {
      toast.error(errMsg(e));
      setRestoring(false);
    }
  };

  return (
    <div data-testid="admin-settings-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-3xl tracking-tight">Settings</h1>
        <Button className="rounded-full" onClick={save} disabled={saving} data-testid="save-settings-btn">
          {saving ? "Saving…" : "Save all settings"}
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="border-2 border-primary/30" data-testid="store-settings-card">
          <CardContent className="p-6 space-y-5">
            <h2 className="font-display text-xl">Online ordering</h2>
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium">{settings.store_open ? "Store is OPEN" : "Store is CLOSED"}</p>
                <p className="text-xs text-muted-foreground">When closed, customers can browse but cannot order.</p>
              </div>
              <Switch checked={!!settings.store_open} onCheckedChange={(v) => set("store_open", v)} data-testid="settings-store-open-switch" />
            </div>
            <div className="space-y-2">
              <Label>Message shown when closed</Label>
              <Textarea rows={2} value={settings.closed_message || ""} onChange={(e) => set("closed_message", e.target.value)} data-testid="closed-message-input" />
            </div>
            <div className="flex items-center justify-between gap-4 border-t pt-4">
              <div>
                <p className="text-sm font-medium">Scheduled hours</p>
                <p className="text-xs text-muted-foreground">Automatically close ordering outside these times (server time).</p>
              </div>
              <Switch checked={!!schedule.enabled} onCheckedChange={(v) => set("schedule", { ...schedule, enabled: v })} data-testid="schedule-enabled-switch" />
            </div>
            {schedule.enabled && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Open at</Label>
                  <Input type="time" value={schedule.open_time || "09:00"} onChange={(e) => set("schedule", { ...schedule, open_time: e.target.value })} data-testid="schedule-open-input" />
                </div>
                <div className="space-y-2">
                  <Label>Close at</Label>
                  <Input type="time" value={schedule.close_time || "17:00"} onChange={(e) => set("schedule", { ...schedule, close_time: e.target.value })} data-testid="schedule-close-input" />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card data-testid="business-settings-card">
          <CardContent className="p-6 space-y-4">
            <h2 className="font-display text-xl">Business details</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Telephone</Label>
                <Input value={business.phone || ""} onChange={(e) => setB("phone", e.target.value)} data-testid="business-phone-input" />
              </div>
              <div className="space-y-2">
                <Label>Public email</Label>
                <Input type="email" value={business.email || ""} onChange={(e) => setB("email", e.target.value)} data-testid="business-email-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input value={business.address || ""} onChange={(e) => setB("address", e.target.value)} data-testid="business-address-input" />
            </div>
            <div className="space-y-2">
              <Label>Google Maps share link</Label>
              <Input type="url" placeholder="https://maps.app.goo.gl/…" value={business.map_link || ""} onChange={(e) => setB("map_link", e.target.value)} data-testid="business-map-link-input" />
              <p className="text-xs text-muted-foreground">Find the shop on Google Maps → Share → copy the link. The footer address will open this when clicked.</p>
            </div>
            <div className="space-y-2">
              <Label>Order & enquiry notifications email</Label>
              <Input type="email" placeholder="Where new order alerts are sent" value={settings.notify_email || ""} onChange={(e) => set("notify_email", e.target.value)} data-testid="notify-email-input" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Opening hours</Label>
                <Button variant="outline" size="sm" onClick={() => setB("opening_hours", [...hours, { day: "", hours: "" }])} data-testid="add-hours-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Add</Button>
              </div>
              {hours.map((h, i) => (
                <div key={i} className="flex gap-2" data-testid={`hours-row-${i}`}>
                  <Input placeholder="Day(s)" value={h.day} onChange={(e) => setB("opening_hours", hours.map((x, xi) => xi === i ? { ...x, day: e.target.value } : x))} data-testid={`hours-day-${i}`} />
                  <Input placeholder="Hours" value={h.hours} onChange={(e) => setB("opening_hours", hours.map((x, xi) => xi === i ? { ...x, hours: e.target.value } : x))} data-testid={`hours-value-${i}`} />
                  <Button variant="ghost" size="icon" className="text-destructive shrink-0" onClick={() => setB("opening_hours", hours.filter((_, xi) => xi !== i))} aria-label="Remove"><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card data-testid="delivery-settings-card">
          <CardContent className="p-6 space-y-4">
            <h2 className="font-display text-xl">Delivery</h2>
            <div className="space-y-2">
              <Label>Delivery days</Label>
              <p className="text-xs text-muted-foreground">
                Customers can only pick these days at checkout. Homepage shows: "Local delivery, {DAY_WORDS[deliveryDays.length].toLowerCase()} {deliveryDays.length === 1 ? "day" : "days"} a week".
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1">
                {WEEK_DAYS.map((d) => (
                  <label key={d.v} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`delivery-day-${d.label.toLowerCase()}`}>
                    <Checkbox checked={deliveryDays.includes(d.v)} onCheckedChange={() => toggleDay(d.v)} />
                    {d.label}
                  </label>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Delivery information</Label>
              <Textarea rows={3} value={delivery.info || ""} onChange={(e) => setD("info", e.target.value)} data-testid="delivery-info-input" />
            </div>
            <div className="space-y-2">
              <Label>Delivery postcodes</Label>
              <p className="text-xs text-muted-foreground">
                Only these postcodes can order delivery. Prefixes cover whole areas (e.g. "NP11" or "NP11 4"); full postcodes for single streets. Spaces and letter case are ignored. Leave empty to allow all postcodes.
              </p>
              {postcodes.map((p, i) => (
                <div key={i} className="flex gap-2 items-center" data-testid={`postcode-row-${i}`}>
                  <Input value={p} onChange={(e) => setD("postcodes", postcodes.map((x, xi) => xi === i ? e.target.value : x))} data-testid={`postcode-entry-${i}`} />
                  <Button variant="ghost" size="icon" className="text-destructive shrink-0" onClick={() => setD("postcodes", postcodes.filter((_, xi) => xi !== i))} aria-label="Remove postcode"><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
              <div className="flex gap-2">
                <Input placeholder="e.g. NP11 or NP11 4EL" value={newPc} onChange={(e) => setNewPc(e.target.value)} data-testid="new-postcode-input" />
                <Button variant="outline" onClick={() => {
                  const v = newPc.trim().toUpperCase();
                  if (v && !postcodes.includes(v)) {
                    setD("postcodes", [...postcodes, v]);
                    setNewPc("");
                  }
                }} data-testid="add-postcode-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Delivery areas (comma separated)</Label>
              <Input value={Array.isArray(delivery.areas) ? delivery.areas.join(", ") : delivery.areas || ""} onChange={(e) => setD("areas", e.target.value)} data-testid="delivery-areas-input" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Fee (£)</Label>
                <Input type="number" step="0.01" value={delivery.fee ?? ""} onChange={(e) => setD("fee", e.target.value)} data-testid="delivery-fee-input" />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label>Free over (£)</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">{delivery.free_delivery_enabled !== false ? "On" : "Off"}</span>
                    <Switch checked={delivery.free_delivery_enabled !== false} onCheckedChange={(v) => setD("free_delivery_enabled", v)} data-testid="free-delivery-switch" />
                  </div>
                </div>
                <Input type="number" step="0.01" value={delivery.free_over ?? ""} onChange={(e) => setD("free_over", e.target.value)} disabled={delivery.free_delivery_enabled === false} data-testid="delivery-free-over-input" />
              </div>
              <div className="space-y-2">
                <Label>Min order (£)</Label>
                <Input type="number" step="0.01" value={delivery.min_order ?? ""} onChange={(e) => setD("min_order", e.target.value)} data-testid="delivery-min-order-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Delivery notes</Label>
              <Textarea rows={2} value={delivery.notes || ""} onChange={(e) => setD("notes", e.target.value)} data-testid="delivery-notes-input" />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="email-settings-card">
          <CardContent className="p-6 space-y-4">
            <h2 className="font-display text-xl">Email</h2>
            <p className="text-xs text-muted-foreground -mt-2">
              All emails (login codes, order confirmations, invoices) are sent from the platform-managed address with the "Eileen Jenkins Florist" sender name. Sending from your own domain (e.g. no-reply@eileenjenkinsflorist.co.uk) isn't supported by the managed sender.
            </p>
            <div className="border-t pt-4">
              <p className="text-sm font-medium">Test order confirmation email</p>
              <p className="text-xs text-muted-foreground mb-3">
                Creates a test order (marked TEST, visible in Orders) and sends the customer confirmation email to your admin address.
              </p>
              <Button variant="outline" onClick={sendTestOrderEmail} disabled={testingEmail} data-testid="send-test-order-email-btn">
                {testingEmail ? "Sending…" : "Send test order confirmation"}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="notification-settings-card">
          <CardContent className="p-6 space-y-4">
            <h2 className="font-display text-xl">Order notification sound</h2>
            <p className="text-xs text-muted-foreground -mt-2">
              Plays in the admin panel when a new order arrives, then repeats every 5 minutes while orders stay "New" — accept/advance the order to stop it. Use the bell in the top bar to mute or unmute. If you hear nothing: browsers block sound until you interact with a page — either click anything in the admin once, or allow sound permanently via the padlock icon in the address bar → Site settings → Sound → Allow.
            </p>
            <div className="space-y-2">
              {NOTIFICATION_SOUNDS.map((s) => (
                <div key={s.id} className="flex items-center justify-between gap-3 border rounded-lg px-3 py-2" data-testid={`notif-sound-${s.id}`}>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="notif-sound" checked={(notif.sound || "chime") === s.id} onChange={() => setN("sound", s.id)} className="accent-primary" />
                    {s.label}
                  </label>
                  <Button variant="ghost" size="icon" onClick={() => playNotificationSound({ sound: s.id })} aria-label={`Preview ${s.label}`} data-testid={`notif-preview-${s.id}`}>
                    <Play className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              <div className="flex items-center justify-between gap-3 border rounded-lg px-3 py-2" data-testid="notif-sound-custom">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="radio" name="notif-sound" checked={notif.sound === "custom"} onChange={() => notif.custom_path && setN("sound", "custom")} disabled={!notif.custom_path} className="accent-primary" />
                  My uploaded sound{notif.custom_name ? <span className="text-xs text-muted-foreground">({notif.custom_name})</span> : null}
                </label>
                {notif.custom_path && (
                  <Button variant="ghost" size="icon" onClick={() => playNotificationSound(notif)} aria-label="Preview custom sound" data-testid="notif-preview-custom">
                    <Play className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
            <div className="border-t pt-4">
              <p className="text-sm font-medium">Upload your own sound</p>
              <p className="text-xs text-muted-foreground mb-2">MP3, WAV, OGG or M4A, max 10MB. Short sounds work best.</p>
              <Input type="file" accept="audio/*" onChange={(e) => uploadSound(e.target.files?.[0])} className="max-w-xs" data-testid="notif-sound-upload" />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="backup-settings-card">
          <CardContent className="p-6 space-y-4">
            <h2 className="font-display text-xl">Backup &amp; restore</h2>
            <p className="text-xs text-muted-foreground -mt-2">
              Everything you can change — products, categories, prices, stock, settings, pages, FAQs, testimonials, gallery, orders and admin accounts — is stored in the database and survives restarts and redeployments automatically. For extra safety, download a full backup to keep off the server.
            </p>
            <div className="border-t pt-4">
              <p className="text-sm font-medium">Download full backup</p>
              <p className="text-xs text-muted-foreground mb-3">One JSON file with the entire site. Download after big changes.</p>
              <Button variant="outline" onClick={downloadBackup} data-testid="download-backup-btn">Download backup</Button>
            </div>
            <div className="border-t pt-4">
              <p className="text-sm font-medium">Restore from backup</p>
              <p className="text-xs text-muted-foreground mb-3">Replaces everything on the site with the contents of a backup file.</p>
              <div className="flex gap-2 items-center flex-wrap">
                <Input type="file" accept="application/json,.json" onChange={(e) => setRestoreFile(e.target.files?.[0] || null)} className="max-w-xs" data-testid="restore-file-input" />
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" disabled={!restoreFile || restoring} data-testid="restore-backup-btn">
                      {restoring ? "Restoring…" : "Restore"}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Restore this backup?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This permanently replaces ALL current products, settings, content and orders with the backup file's contents. This cannot be undone unless you have downloaded a current backup first.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="restore-cancel-btn">Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={restoreBackup} data-testid="restore-confirm-btn">Yes, restore everything</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
            <div className="border-t pt-4">
              <p className="text-sm font-medium">Cloud snapshots</p>
              <p className="text-xs text-muted-foreground mb-3">
                Saved to cloud storage that survives redeployments and database resets — even if the database is ever wiped, these remain. A snapshot is also saved automatically every night.
              </p>
              <Button variant="outline" onClick={saveSnapshot} disabled={savingSnapshot} data-testid="save-snapshot-btn">
                {savingSnapshot ? "Saving…" : "Save cloud snapshot now"}
              </Button>
              {snapshots.length > 0 && (
                <ul className="mt-3 space-y-2" data-testid="snapshot-list">
                  {snapshots.map((s, i) => (
                    <li key={s.path} className="flex items-center justify-between gap-3 text-sm border rounded-lg px-3 py-2" data-testid={`snapshot-row-${i}`}>
                      <span className="text-muted-foreground">
                        {new Date(s.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        <span className="text-xs"> · {s.records} records</span>
                      </span>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" size="sm" data-testid={`snapshot-restore-${i}`}>Restore</Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Restore this snapshot?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This permanently replaces ALL current products, settings, content and orders with this snapshot's contents. Consider downloading a current backup first.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => restoreSnapshot(s.path)} data-testid={`snapshot-restore-confirm-${i}`}>Yes, restore everything</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </CardContent>
        </Card>

        <Card data-testid="paypal-settings-card">
          <CardContent className="p-6 space-y-4">
            <h2 className="font-display text-xl">PayPal payments</h2>
            <p className="text-xs text-muted-foreground -mt-2">
              Get your Client ID and Secret from the PayPal Developer dashboard (developer.paypal.com). Card details are handled entirely by PayPal — nothing sensitive touches this site.
            </p>
            <div className="flex items-center justify-between">
              <Label>Accept PayPal payments online</Label>
              <Switch checked={!!paypal.enabled} onCheckedChange={(v) => setP("enabled", v)} data-testid="paypal-enabled-switch" />
            </div>
            <div className="space-y-2">
              <Label>Mode</Label>
              <Select value={paypal.mode || "sandbox"} onValueChange={(v) => setP("mode", v)}>
                <SelectTrigger data-testid="paypal-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sandbox" data-testid="paypal-mode-sandbox">Sandbox (testing)</SelectItem>
                  <SelectItem value="live" data-testid="paypal-mode-live">Live (real payments)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Client ID</Label>
              <Input value={paypal.client_id || ""} onChange={(e) => setP("client_id", e.target.value)} data-testid="paypal-client-id-input" autoComplete="off" />
            </div>
            <div className="space-y-2">
              <Label>Secret</Label>
              <Input type="password" value={paypal.secret || ""} onChange={(e) => setP("secret", e.target.value)} data-testid="paypal-secret-input" autoComplete="new-password" />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="statuses-settings-card">
          <CardContent className="p-6 space-y-4">
            <h2 className="font-display text-xl">Order statuses</h2>
            <p className="text-xs text-muted-foreground -mt-2">The stages an order moves through. "Completed" triggers the customer invoice email.</p>
            {statuses.map((s, i) => (
              <div key={i} className="flex gap-2 items-center" data-testid={`status-row-${i}`}>
                <Input value={s} onChange={(e) => set("order_statuses", statuses.map((x, xi) => xi === i ? e.target.value : x))} data-testid={`status-name-${i}`} />
                <Button variant="ghost" size="icon" className="text-destructive shrink-0" disabled={statuses.length <= 1 || s === "Completed"} onClick={() => set("order_statuses", statuses.filter((_, xi) => xi !== i))} aria-label="Remove status"><Trash2 className="h-4 w-4" /></Button>
              </div>
            ))}
            <div className="flex gap-2">
              <Input placeholder="New status name" value={newStatus} onChange={(e) => setNewStatus(e.target.value)} data-testid="new-status-input" />
              <Button variant="outline" onClick={() => {
                if (newStatus.trim() && !statuses.includes(newStatus.trim())) {
                  set("order_statuses", [...statuses, newStatus.trim()]);
                  setNewStatus("");
                }
              }} data-testid="add-status-btn"><Plus className="h-4 w-4 mr-1" /> Add</Button>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="seo-settings-card">
          <CardContent className="p-6 space-y-4">
            <h2 className="font-display text-xl">SEO defaults</h2>
            <div className="space-y-2">
              <Label>Site title</Label>
              <Input value={seo.site_title || ""} onChange={(e) => setSeo("site_title", e.target.value)} data-testid="seo-title-input" />
            </div>
            <div className="space-y-2">
              <Label>Meta description</Label>
              <Textarea rows={2} value={seo.meta_description || ""} onChange={(e) => setSeo("meta_description", e.target.value)} data-testid="seo-description-input" />
            </div>
            <div className="space-y-2">
              <Label>Social share image URL (optional)</Label>
              <Input value={seo.og_image || ""} onChange={(e) => setSeo("og_image", e.target.value)} data-testid="seo-og-image-input" />
            </div>
            <div className="space-y-2">
              <Label>Currency</Label>
              <Select value={settings.currency || "GBP"} onValueChange={(v) => set("currency", v)}>
                <SelectTrigger data-testid="currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="GBP">GBP (£)</SelectItem>
                  <SelectItem value="EUR">EUR (€)</SelectItem>
                  <SelectItem value="USD">USD ($)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="security-card">
          <CardContent className="p-6 space-y-4">
            <h2 className="font-display text-xl">Admin password</h2>
            <div className="space-y-2">
              <Label>Current password</Label>
              <Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="current-password-input" autoComplete="current-password" />
            </div>
            <div className="space-y-2">
              <Label>New password (min 8 characters)</Label>
              <Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="new-password-input" autoComplete="new-password" />
            </div>
            <Button variant="outline" onClick={changePassword} disabled={!pw.current_password || pw.new_password.length < 8} data-testid="change-password-btn">
              Change password
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
